import { SwPartNumber } from '../../shared/models/sw-part-number/sw-part-number';
import { Translatable } from '../../shared/models/base/translatable';
import { TranslationState } from '../../shared/enums/translation-state';
import { TranslationStateBgColor } from '../../shared/enums/translation-state-bg-color';
import { TranslationService } from '../services/translation.service';
import { TranslationStateDisplayConfig } from './translation-state-display-config';
import { SwPartNumberState } from '../../shared/enums/sw-part-number-state';
import { LogLevel } from '../../shared/enums/report/log-level';
import { LogLevelColor } from '../../shared/enums/report/log-level-color';
import { LogLevelDisplayConfig } from './log-level-display-config';

export class CommonUtils {
  public static translationStateDisplayConfig = new Map([
    [
      TranslationState.MainLanguage,
      new TranslationStateDisplayConfig(
        TranslationStateBgColor.MainLanguage,
        0,
        pascalCaseToWords(TranslationState.MainLanguage)
      ),
    ],
    [
      TranslationState.Translated,
      new TranslationStateDisplayConfig(
        TranslationStateBgColor.Translated,
        1,
        pascalCaseToWords(TranslationState.Translated)
      ),
    ],
    [
      TranslationState.ManuallyTranslated,
      new TranslationStateDisplayConfig(
        TranslationStateBgColor.ManuallyTranslated,
        2,
        pascalCaseToWords(TranslationState.ManuallyTranslated)
      ),
    ],
    [
      TranslationState.MainLanguageUpdated,
      new TranslationStateDisplayConfig(
        TranslationStateBgColor.MainLanguageUpdated,
        3,
        pascalCaseToWords(TranslationState.MainLanguageUpdated)
      ),
    ],
    [
      TranslationState.TranslationPending,
      new TranslationStateDisplayConfig(
        TranslationStateBgColor.TranslationPending,
        5,
        pascalCaseToWords(TranslationState.TranslationPending)
      ),
    ],
    [
      TranslationState.TranslationMissing,
      new TranslationStateDisplayConfig(
        TranslationStateBgColor.TranslationMissing,
        100,
        pascalCaseToWords(TranslationState.TranslationMissing)
      ),
    ],
    [
      TranslationState.PartiallyTranslated,
      new TranslationStateDisplayConfig(
        TranslationStateBgColor.PartiallyTranslated,
        100,
        pascalCaseToWords(TranslationState.PartiallyTranslated)
      ),
    ],
  ]);

  private static readonly logLevelDisplayConfig = new Map([
    [LogLevel.Info, new LogLevelDisplayConfig(LogLevelColor.Info, 'ℹ️', LogLevel.Info)],
    [LogLevel.Warning, new LogLevelDisplayConfig(LogLevelColor.Warning, '⚠️', LogLevel.Warning)],
    [LogLevel.Error, new LogLevelDisplayConfig(LogLevelColor.Error, '❌', LogLevel.Error)],
  ]);

  public static getFriendlySwPartNumber(swPartNumber: SwPartNumber): string {
    return this.maskSwPartNumberMinorRelease(swPartNumber.number) ?? '';
  }

  public static maskSwPartNumberMinorRelease(minorRelease?: string): string | undefined {
    return minorRelease?.replace('000', 'DEV');
  }

  /**
   *
   * @param string expects a string in format of {majorRelease}.{minorRelease} (e.g. NW405-A.000), if the format is not correct, it will return the original string
   * @returns the string with the minor release masked (e.g. NW405-A.DEV)
   */
  public static maskSwPartNumber(string?: string): string | undefined {
    if (!string) {
      return string;
    }
    const parts = string.split('.');
    if (parts.length != 2) {
      return string;
    }
    const [majorRelease, minorRelease] = parts;
    if (!majorRelease || !minorRelease) {
      return string;
    }

    return `${majorRelease}.${this.maskSwPartNumberMinorRelease(minorRelease)}`;
  }

  public static isToday(date: string | Date | undefined): boolean {
    if (!date) return false;
    const d = new Date(date);
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  }

  public static truncateString(text: string | undefined, maxLength = 100): string {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength - 3) + '...' : text;
  }

  public static decorateTranslationStateForLanguageCode(
    translatable: Translatable,
    languageCode: string | null = null
  ): void {
    if (!languageCode) {
      languageCode = translatable.languageCode ?? TranslationService.defaultLanguageCode();
    }

    const isDefaultLanguageCode = languageCode == TranslationService.defaultLanguageCode();

    if (translatable.languageTranslationStatesDocumentation) {
      const translationDocumentation = translatable.languageTranslationStatesDocumentation.find(
        (ts) => ts.languageCode == languageCode
      );
      if (translationDocumentation) {
        translatable.translationStateDocumentation = this.FinalizeTranslationStateForMainLanguage(
          isDefaultLanguageCode,
          translationDocumentation.translationState
        );
      }
    }

    if (translatable.languageTranslationStatesTerminal) {
      const translationTerminal = translatable.languageTranslationStatesTerminal.find(
        (ts) => ts.languageCode == languageCode
      );
      if (translationTerminal) {
        translatable.translationStateTerminal = this.FinalizeTranslationStateForMainLanguage(
          isDefaultLanguageCode,
          translationTerminal.translationState
        );
      }
    }
  }

  public static friendlyTranslationState(translationState?: TranslationState): string {
    if (!translationState) {
      return 'N/A';
    }
    return this.translationStateDisplayConfig.get(translationState)?.displayName ?? 'N/A';
  }

  public static translationStateToColor(translationState?: TranslationState): string {
    if (!translationState) {
      return 'bg-white';
    }

    const config = CommonUtils.translationStateDisplayConfig.get(translationState);
    if (!config) {
      return 'bg-white';
    }
    return config.color;
  }

  // TODO: move to LogLevelUtils
  public static logLevelToIcon(logLevel?: LogLevel): string {
    if (!logLevel) {
      return '❓';
    }

    const config = this.logLevelDisplayConfig.get(logLevel);
    if (!config) {
      return '❓';
    }
    return config.icon;
  }

  public static logLevelToDisplayName(logLevel?: LogLevel): string {
    if (!logLevel) {
      return 'Unknown';
    }

    const config = this.logLevelDisplayConfig.get(logLevel);
    if (!config) {
      return 'Unknown';
    }
    return config.displayName;
  }

  public static logLevelToColor(logLevel?: LogLevel): string {
    if (!logLevel) {
      return 'bg-white';
    }

    const config = this.logLevelDisplayConfig.get(logLevel);
    if (!config) {
      return 'bg-white';
    }
    return config.color;
  }

  public static swPartNumberStateToSymbol(translationState?: string): string {
    switch (translationState) {
      case SwPartNumberState.Snapshot:
        return '🔒';
      case SwPartNumberState.Released:
        return '🚀';
      case SwPartNumberState.Dev:
        return '🔧';
    }
    return '';
  }

  public static breakIntoLines(str?: string): string[] {
    return (str || '').replaceAll('\r', '').split('\n');
  }

  public static deepEqual<T>(obj1: T, obj2: T): boolean {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  }

  public static downloadFile(blob: Blob, fileName: string): boolean {
    if (blob.size == 0) {
      // do not download empty logs
      return false;
    }

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Revoke the object URL to free up memory
    window.URL.revokeObjectURL(url);
    return true;
  }

  private static FinalizeTranslationStateForMainLanguage(
    isDefaultLanguageCode: boolean,
    originalTranslationState?: TranslationState
  ): TranslationState | undefined {
    return isDefaultLanguageCode && originalTranslationState == 'Translated'
      ? TranslationState.MainLanguage
      : originalTranslationState;
  }
}

export function getPropertyName<T>(key: keyof T): string {
  return key as string;
}

export function pascalCaseToWords(input: string): string {
  return input.replace(/([a-z])([A-Z])/g, '$1 $2').trim();
}

export function snakeCaseToWords(input: string): string {
  return input
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim();
}

export function errorToString(error: unknown): string {
  if (error instanceof Error) {
    return `${error.name}: ${error.message}\n${error.stack ?? ''}`;
  }

  if (typeof error === 'string') {
    return error;
  }

  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}
